import React, {FC, PropsWithChildren, ReactNode, useLayoutEffect, useRef, useState} from 'react';
import styled, {css} from 'styled-components';

import {buildHoverParentClassName} from '../../../../../libs/shared/core/src/helpers/browser/hoverHelpers';
import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/components/trans';
import {ContactSourcesEnum} from '../../../../../libs/shared/core/src/models/api/contactModel';
import {ContactCardPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {getFirstContactEntryPoint} from '../../../../store/data/applications/applicationsSelectors';
import {activateContactEntryPointAction} from '../../../../store/data/applications/selectedSideFrameIdActions';
import {ContactCardLocationsEnum} from '../../../../store/panels/contacts/contactCard/contactCardPanelState';
import {CopyButton} from '../../../common/buttons/copyButton';
import {NavigateToPluginDialog} from '../../../common/dialogs/navigateToPluginDialog';
import {ContactCardActionsWrapper} from '../../common/contactCardActionsWrapper';
import {ContactCardExternalLinkButton} from '../../common/contactCardExternalLinkButton';

/*
 * Constants.
 */

const singleLineItemScrollHeight = 27; // px.

/*
 * Props.
 */

interface ContactCardSectionItemProps {
  title?: ReactNode;
  copyInfo: string;
  handle?: string;
  source?: ContactSourcesEnum;
  location?: ContactCardLocationsEnum;
  panelId?: ContactCardPanelId;
}

/*
 * Style.
 */

const StyledSectionItemWrapperDiv = styled.div`
  display: contents;
`;

const StyledSectionItemTitleDiv = styled.div`
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.small};
  line-height: 16px;
  height: 16px;
  color: ${(p) => p.theme.greys.shade70};
  max-width: 124px;

  &:not(:empty) {
    margin-right: 12px;
  }
`;

interface StyledSectionItemContentDivProps {
  $shouldAddNegativeBottomMargin: boolean;
}
const StyledSectionItemContentDiv = styled.div<StyledSectionItemContentDivProps>`
  position: relative;
  overflow: hidden;
  line-height: 16px;
  padding-top: 5px;
  margin-top: -5px;

  // We need negative margin so the button is not cut off from the overflow hidden.
  ${(p) =>
    p.$shouldAddNegativeBottomMargin &&
    css`
      margin-bottom: -14px;
    `}
`;

const StyledItemContentChildWrapperDiv = styled.div`
  line-height: 16px;
`;

/*
 * Component.
 */

export const ContactCardSectionItem: FC<PropsWithChildren<ContactCardSectionItemProps>> = (props) => {
  const {title, children, copyInfo, handle, source, location, panelId} = props;
  const [shouldAddNegativeBottomMargin, setShouldAddNegativeBottomMargin] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const theme = useUserTheme();
  const dispatch = useAppDispatch();

  const [shouldShowDialog, setShouldShowDialog] = useState(false);

  const contactEntryPoint = useAppSelector(getFirstContactEntryPoint);
  const isCallingAvailable = contactEntryPoint && source === ContactSourcesEnum.PHONE;

  useLayoutEffect(() => {
    if (!contentRef.current) {
      return;
    }
    setShouldAddNegativeBottomMargin(contentRef.current.scrollHeight <= singleLineItemScrollHeight);
  }, []);

  const onCallClick = () => {
    if (!contactEntryPoint || !panelId || !handle) {
      return;
    }

    switch (location) {
      case ContactCardLocationsEnum.CALENDAR:
      case ContactCardLocationsEnum.CALENDAR_NEW_EVENT:
        // If we're in calendar, we show the modal instead of opening the plugin directly.
        setShouldShowDialog(true);
        return;
      default:
        dispatch(activateContactEntryPointAction(contactEntryPoint, handle, panelId));
    }
  };

  return (
    <>
      <StyledSectionItemWrapperDiv className={buildHoverParentClassName()}>
        <StyledSectionItemTitleDiv>
          <TooltipCoordinator render={() => title}>
            <TooltipOverflowCondition>{title}</TooltipOverflowCondition>
          </TooltipCoordinator>
        </StyledSectionItemTitleDiv>
        <StyledSectionItemContentDiv
          ref={contentRef}
          $shouldAddNegativeBottomMargin={shouldAddNegativeBottomMargin}
        >
          <StyledItemContentChildWrapperDiv>{children}</StyledItemContentChildWrapperDiv>
          <ContactCardActionsWrapper>
            {isCallingAvailable && (
              <IconButton
                iconName="phoneSmall"
                color={theme.greys.shade70}
                label={<Trans id="contact-card-section-item-call">Call</Trans>}
                onClick={onCallClick}
              />
            )}
            {maybeRenderExternalLinkButton(handle, source)}
            <CopyButton copyValue={copyInfo} />
          </ContactCardActionsWrapper>
        </StyledSectionItemContentDiv>
      </StyledSectionItemWrapperDiv>
      {shouldShowDialog && (
        <NavigateToPluginDialog
          contactEntryPoint={contactEntryPoint}
          handle={handle}
          panelId={panelId}
          onRequestClose={() => {
            setShouldShowDialog(false);
          }}
        />
      )}
    </>
  );
};

/*
 * Helpers.
 */

function maybeRenderExternalLinkButton(handle?: string, source?: ContactSourcesEnum) {
  if (!handle || !source) {
    return null;
  }
  return <ContactCardExternalLinkButton handle={handle} source={source} />;
}
